import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserCheck, UserX, Key, Trash2, MoreVertical, StickyNote, Search, Eye, EyeOff, AlertTriangle, Save, Download, Plus, X, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  approveAgent, rejectAgent, activateAgent, deactivateAgent, updateAgentTeams,
  updateAgentRole, updateAgentProfile, adminResetPassword, deleteAgent, addAgentNote, getTeams,
  getLeads, createLead, updateLead, addLeadNote, deleteLead,
} from '../utils/api';
import { quoteCount } from '../components/AdvancedQuoteFields';
import AgentLayout from '../components/AgentLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const coverageLabels = { final_expense: 'Final Expense', medicare: 'Medicare', life: 'Life Insurance', health: 'Health & Supplemental' };
const sourceLabels = { web: 'Website', referral: 'Referral', purchased: 'Purchased', door_knock: 'Door Knock', phone: 'Phone', other: 'Other' };
const leadStatusLabels = { new: 'New', contacted: 'Contacted', qualified: 'Qualified', proposal: 'Proposal', closed_won: 'Closed Won', closed_lost: 'Closed Lost' };
const agentStatusLabels = { approved: 'Approved', pending: 'Pending', rejected: 'Rejected', deactivated: 'Deactivated' };
const allStatusLabels = { ...leadStatusLabels, ...agentStatusLabels };
const roleLabels = { agent: 'Agent', admin: 'Admin', super_admin: 'Super Admin' };

const statusStyles = {
  approved: 'bg-green-100 text-green-800', pending: 'bg-yellow-100 text-yellow-800',
  rejected: 'bg-red-100 text-red-800', deactivated: 'bg-gray-100 text-gray-700',
  new: 'bg-blue-100 text-blue-800', contacted: 'bg-yellow-100 text-yellow-800',
  qualified: 'bg-purple-100 text-purple-800', proposal: 'bg-indigo-100 text-indigo-800',
  closed_won: 'bg-green-100 text-green-800', closed_lost: 'bg-red-100 text-red-800',
};
const typeStyles = {
  Agent: 'bg-slate-200 text-slate-800', Quote: 'bg-green-100 text-green-800',
  Contact: 'bg-blue-100 text-blue-800', 'Career Interest': 'bg-orange-100 text-orange-800',
  Newsletter: 'bg-purple-100 text-purple-800', Manual: 'bg-gray-100 text-gray-700',
};

const AdminUsers = () => {
  const navigate = useNavigate();
  const [agentInfo, setAgentInfo] = useState(null);
  const [agents, setAgents] = useState([]);
  const [leads, setLeads] = useState([]);
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [draft, setDraft] = useState({});
  const [noteText, setNoteText] = useState('');
  const [resetPwOpen, setResetPwOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPw, setShowNewPw] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const emptyContact = { firstName: '', lastName: '', email: '', phone: '', zipCode: '', coverageInterest: 'final_expense', source: 'referral', notes: '' };
  const [newContact, setNewContact] = useState(emptyContact);

  const token = localStorage.getItem('agentToken');

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const [agentsRes, teamsRes, leadsRes] = await Promise.all([
        axios.get(`${API}/admin/agents`, { headers }),
        getTeams(token),
        getLeads(token),
      ]);
      setAgents(agentsRes.data.agents || []);
      setTeams(teamsRes.teams || []);
      setLeads(leadsRes.leads || []);
    } catch (e) {
      console.error('Error:', e);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const init = async () => {
      const info = localStorage.getItem('agentInfo');
      if (!token || !info) { navigate('/agent/login'); return; }
      const parsed = JSON.parse(info);
      if (parsed.role !== 'admin' && parsed.role !== 'super_admin') {
        toast.error('Admin access required');
        navigate('/agent/dashboard');
        return;
      }
      setAgentInfo(parsed);
      await loadData();
    };
    init();
  }, [navigate, token, loadData]);

  // Build unified people list
  const people = useMemo(() => [
    ...agents.map(a => ({ kind: 'agent', id: a.id, name: a.fullName, email: a.email, phone: a.phone, type: 'Agent', team: (a.teams || []).join(', '), role: a.role, status: a.status, raw: a })),
    ...leads.map(l => ({ kind: 'lead', id: l.id, name: `${l.firstName || ''} ${l.lastName || ''}`.trim(), email: l.email, phone: l.phone, type: l.formType || 'Manual', team: '', role: '', status: l.status, raw: l })),
  ], [agents, leads]);

  const filtered = useMemo(() => people.filter(p => {
    if (categoryFilter === 'agents' && p.kind !== 'agent') return false;
    if (categoryFilter === 'leads' && p.kind !== 'lead') return false;
    if (typeFilter !== 'all' && p.type !== typeFilter) return false;
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      if (!p.name?.toLowerCase().includes(t) && !p.email?.toLowerCase().includes(t) && !(p.phone || '').includes(searchTerm)) return false;
    }
    return true;
  }), [people, categoryFilter, typeFilter, statusFilter, searchTerm]);

  const exportCSV = () => {
    const headers = ['Category', 'Name', 'Email', 'Phone', 'Team / Type', 'Role', 'Status'];
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = filtered.map(p => [
      p.kind === 'agent' ? 'Agent' : 'Lead',
      p.name,
      p.email || '',
      p.phone || '',
      p.kind === 'agent' ? (p.team || '') : p.type,
      p.kind === 'agent' ? (roleLabels[p.role] || p.role || '') : '',
      allStatusLabels[p.status] || p.status,
    ].map(esc).join(','));
    const csv = [headers.map(esc).join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contacts-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${filtered.length} contacts`);
  };

  const toggleExpand = (p) => {
    if (expandedId === p.id) { setExpandedId(null); return; }
    setExpandedId(p.id);
    setDraft({ ...p.raw });
    setNoteText(''); setResetPwOpen(false); setNewPassword(''); setDeleteConfirm(false);
  };

  // ---- Lead handlers ----
  const saveLead = async () => {
    try {
      await updateLead(draft.id, {
        firstName: draft.firstName, lastName: draft.lastName, email: draft.email, phone: draft.phone,
        zipCode: draft.zipCode, coverageInterest: draft.coverageInterest, source: draft.source,
        formType: draft.formType, status: draft.status,
      }, token);
      toast.success('Contact updated');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };
  const noteLead = async () => {
    if (!noteText.trim()) return;
    try { await addLeadNote(draft.id, noteText, token); toast.success('Note added'); setNoteText(''); const r = await getLeads(token); setLeads(r.leads || []); const updated = (r.leads || []).find(l => l.id === draft.id); if (updated) setDraft({ ...updated }); }
    catch (e) { toast.error('Failed to add note'); }
  };
  const removeLead = async () => {
    try { await deleteLead(draft.id, token); toast.success('Contact deleted'); setExpandedId(null); loadData(); }
    catch (e) { toast.error('Failed to delete'); }
  };

  // ---- Agent handlers ----
  const saveAgentProfile = async () => {
    try {
      await updateAgentProfile(draft.id, { fullName: draft.fullName, phone: draft.phone, email: draft.email }, token);
      toast.success('Agent updated');
      loadData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };
  const agentAction = async (action) => {
    try {
      const fns = { approve: approveAgent, reject: rejectAgent, activate: activateAgent, deactivate: deactivateAgent };
      const r = await fns[action](draft.id, token);
      if (r.success) { toast.success(r.message); loadData(); }
    } catch (e) { toast.error(e.response?.data?.detail || `Failed to ${action}`); }
  };
  const agentRole = async (role) => {
    try { const r = await updateAgentRole(draft.id, role, token); if (r.success) { toast.success(r.message); setDraft({ ...draft, role }); loadData(); } }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to update role'); }
  };
  const agentTeam = async (newTeams) => {
    try { const r = await updateAgentTeams(draft.id, newTeams, token); if (r.success) { toast.success(r.message); setDraft({ ...draft, teams: newTeams }); loadData(); } }
    catch (e) { toast.error('Failed to update teams'); }
  };
  const agentResetPw = async () => {
    if (newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    try { const r = await adminResetPassword(draft.id, newPassword, token); if (r.success) { toast.success(r.message); setResetPwOpen(false); setNewPassword(''); } }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to reset password'); }
  };
  const noteAgent = async () => {
    if (!noteText.trim()) return;
    try { await addAgentNote(draft.id, noteText, token); toast.success('Note added'); setNoteText(''); const r = await axios.get(`${API}/admin/agents`, { headers: { Authorization: `Bearer ${token}` } }); setAgents(r.data.agents || []); const updated = (r.data.agents || []).find(a => a.id === draft.id); if (updated) setDraft({ ...updated }); }
    catch (e) { toast.error('Failed to add note'); }
  };
  const removeAgent = async () => {
    try { const r = await deleteAgent(draft.id, token); if (r.success) { toast.success(r.message); setExpandedId(null); loadData(); } }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const handleCreateContact = async () => {
    if (!newContact.firstName || !newContact.lastName || !newContact.phone) {
      toast.error('First name, last name, and phone are required'); return;
    }
    try {
      const r = await createLead({ ...newContact, formType: 'Manual' }, token);
      if (r.success) { toast.success('Contact added'); setShowCreate(false); setNewContact(emptyContact); loadData(); }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add contact'); }
  };

  const badge = (cls, label, testid) => <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`} data-testid={testid}>{label}</span>;

  const rev = (arr) => (arr || []).reduceRight((acc, x) => { acc.push(x); return acc; }, []);

  const inputCls = 'w-full px-3 py-2 border rounded-md text-sm focus:ring-2 focus:ring-brand-blue focus:outline-none';

  if (isLoading) {
    return (
      <AgentLayout title="User Management">
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div></div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout title="User Management">
      <div data-testid="admin-users-page">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6" data-testid="users-filters">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search name, email, or phone..." className="w-full pl-10 pr-4 py-2 border rounded-md focus:ring-2 focus:ring-brand-blue focus:outline-none text-sm" data-testid="users-search" />
            </div>
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="px-3 py-2 border rounded-md text-sm" data-testid="users-category-filter">
              <option value="all">Everyone</option>
              <option value="agents">Agents only</option>
              <option value="leads">Leads only</option>
            </select>
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-3 py-2 border rounded-md text-sm" data-testid="users-type-filter">
              <option value="all">All Types</option>
              <option value="Agent">Agent</option>
              <option value="Quote">Quote Request</option>
              <option value="Contact">Contact</option>
              <option value="Career Interest">Career Interest</option>
              <option value="Newsletter">Newsletter</option>
              <option value="Manual">Manual</option>
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-3 py-2 border rounded-md text-sm" data-testid="users-status-filter">
              <option value="all">All Status</option>
              <optgroup label="Lead Status">
                {Object.entries(leadStatusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </optgroup>
              <optgroup label="Agent Status">
                {Object.entries(agentStatusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </optgroup>
            </select>
          </div>
          <div className="flex items-center justify-between mt-2 gap-3 flex-wrap">
            <p className="text-xs text-gray-500">Showing {filtered.length} of {people.length} people ({agents.length} agents, {leads.length} leads)</p>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowCreate(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-orange text-white rounded-md text-sm font-semibold hover:bg-orange-600 transition-colors"
                data-testid="new-contact-btn">
                <Plus size={15} /> New Contact
              </button>
              <button onClick={exportCSV} disabled={filtered.length === 0}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-brand-blue text-white rounded-md text-sm font-semibold hover:bg-brand-orange disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                data-testid="export-csv-btn">
                <Download size={15} /> Export CSV
              </button>
            </div>
          </div>
        </div>

        {/* Unified People Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead className="bg-gray-50 border-b">
                <tr className="text-left text-xs font-bold text-gray-600 uppercase tracking-wide">
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Phone</th>
                  <th className="px-4 py-3">Team / Type</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-400">No people match these filters</td></tr>
                )}
                {filtered.map(p => {
                  const isExpanded = expandedId === p.id;
                  return (
                    <React.Fragment key={`${p.kind}-${p.id}`}>
                      <tr className={`hover:bg-gray-50 ${isExpanded ? 'bg-gray-50' : ''}`} data-testid={`person-row-${p.id}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <div className={`w-8 h-8 rounded-full ${p.kind === 'agent' ? 'bg-brand-blue' : 'bg-brand-orange'} flex items-center justify-center text-white text-xs font-bold flex-shrink-0`}>
                              {(p.name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                            </div>
                            <div className="min-w-0">
                              <span className="font-semibold text-gray-900 whitespace-nowrap block">{p.name || 'Unknown'}</span>
                              {p.kind === 'lead' && quoteCount(p.raw) > 0 && (
                                <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full ${quoteCount(p.raw) >= 2 ? 'text-amber-700 bg-amber-100' : 'text-green-700 bg-green-100'}`} data-testid={`um-quote-badge-${p.id}`}><CheckCircle2 size={10} /> {quoteCount(p.raw) >= 2 ? `${quoteCount(p.raw)} quotes` : 'Quote details'}</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{p.email || '—'}</td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{p.phone || '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {p.kind === 'agent'
                            ? (p.team ? <span className="text-gray-700">{p.team}</span> : <span className="text-gray-400">—</span>)
                            : badge(typeStyles[p.type] || 'bg-gray-100 text-gray-700', p.type, `type-badge-${p.id}`)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-700">{p.kind === 'agent' ? (roleLabels[p.role] || p.role || '—') : <span className="text-gray-400">—</span>}</td>
                        <td className="px-4 py-3 whitespace-nowrap">{badge(statusStyles[p.status] || 'bg-gray-100 text-gray-600', allStatusLabels[p.status] || p.status, `status-badge-${p.id}`)}</td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => toggleExpand(p)} className="inline-flex w-8 h-8 items-center justify-center rounded-lg hover:bg-gray-200 text-gray-600" data-testid={`actions-menu-${p.id}`} aria-label="Edit">
                            <MoreVertical size={18} />
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr data-testid={`editor-row-${p.id}`}>
                          <td colSpan={7} className="p-0">
                            <div className="border-t p-4 bg-gray-50 space-y-4" data-testid={`editor-${p.id}`}>
                    {p.kind === 'lead' ? (
                      <>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">First Name</label><input value={draft.firstName || ''} onChange={(e) => setDraft({ ...draft, firstName: e.target.value })} className={inputCls} data-testid={`edit-firstName-${p.id}`} /></div>
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">Last Name</label><input value={draft.lastName || ''} onChange={(e) => setDraft({ ...draft, lastName: e.target.value })} className={inputCls} data-testid={`edit-lastName-${p.id}`} /></div>
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">Phone</label><input value={draft.phone || ''} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} className={inputCls} data-testid={`edit-phone-${p.id}`} /></div>
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">Email</label><input value={draft.email || ''} onChange={(e) => setDraft({ ...draft, email: e.target.value })} className={inputCls} data-testid={`edit-email-${p.id}`} /></div>
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">Zip Code</label><input value={draft.zipCode || ''} onChange={(e) => setDraft({ ...draft, zipCode: e.target.value })} className={inputCls} /></div>
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">Type</label>
                            <select value={draft.formType || 'Manual'} onChange={(e) => setDraft({ ...draft, formType: e.target.value })} className={inputCls} data-testid={`edit-type-${p.id}`}>
                              <option value="Quote">Quote</option><option value="Contact">Contact</option><option value="Career Interest">Career Interest</option><option value="Newsletter">Newsletter</option><option value="Manual">Manual</option>
                            </select>
                          </div>
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">Coverage Interest</label>
                            <select value={draft.coverageInterest || 'final_expense'} onChange={(e) => setDraft({ ...draft, coverageInterest: e.target.value })} className={inputCls}>
                              {Object.entries(coverageLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                          </div>
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">Source</label>
                            <select value={draft.source || 'web'} onChange={(e) => setDraft({ ...draft, source: e.target.value })} className={inputCls}>
                              {Object.entries(sourceLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                          </div>
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">Status</label>
                            <select value={draft.status || 'new'} onChange={(e) => setDraft({ ...draft, status: e.target.value })} className={inputCls} data-testid={`edit-leadstatus-${p.id}`}>
                              {Object.entries(leadStatusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                            </select>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button onClick={saveLead} className="flex items-center gap-1 px-4 py-2 bg-brand-blue text-white rounded-md text-sm font-semibold hover:bg-brand-orange" data-testid={`save-lead-${p.id}`}><Save size={14} /> Save Changes</button>
                          <button onClick={() => setDeleteConfirm(!deleteConfirm)} className="flex items-center gap-1 px-3 py-2 bg-red-100 text-red-700 rounded-md text-sm hover:bg-red-200" data-testid={`delete-lead-${p.id}`}><Trash2 size={14} /> Delete</button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">Full Name</label><input value={draft.fullName || ''} onChange={(e) => setDraft({ ...draft, fullName: e.target.value })} className={inputCls} data-testid={`edit-fullName-${p.id}`} /></div>
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">Phone</label><input value={draft.phone || ''} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} className={inputCls} data-testid={`edit-agentphone-${p.id}`} /></div>
                          <div><label className="block text-xs font-bold text-gray-600 mb-1">Email</label><input value={draft.email || ''} onChange={(e) => setDraft({ ...draft, email: e.target.value })} className={inputCls} data-testid={`edit-agentemail-${p.id}`} /></div>
                        </div>
                        <div className="flex flex-wrap gap-2 items-center">
                          <button onClick={saveAgentProfile} className="flex items-center gap-1 px-4 py-2 bg-brand-blue text-white rounded-md text-sm font-semibold hover:bg-brand-orange" data-testid={`save-agent-${p.id}`}><Save size={14} /> Save Profile</button>
                          {draft.status === 'pending' && (
                            <>
                              <button onClick={() => agentAction('approve')} className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700"><UserCheck size={14} /> Approve</button>
                              <button onClick={() => agentAction('reject')} className="flex items-center gap-1 px-3 py-2 bg-red-600 text-white rounded-md text-sm hover:bg-red-700"><UserX size={14} /> Reject</button>
                            </>
                          )}
                          {draft.status === 'approved' && (
                            <button onClick={() => agentAction('deactivate')} className="flex items-center gap-1 px-3 py-2 bg-yellow-600 text-white rounded-md text-sm hover:bg-yellow-700"><UserX size={14} /> Deactivate</button>
                          )}
                          {(draft.status === 'deactivated' || draft.status === 'rejected') && (
                            <button onClick={() => agentAction('activate')} className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700"><UserCheck size={14} /> Activate</button>
                          )}
                          <button onClick={() => setResetPwOpen(!resetPwOpen)} className="flex items-center gap-1 px-3 py-2 bg-slate-700 text-white rounded-md text-sm hover:bg-slate-800"><Key size={14} /> Reset Password</button>
                          {agentInfo?.role === 'super_admin' && draft.id !== agentInfo?.id && (
                            <select value={draft.role || 'agent'} onChange={(e) => agentRole(e.target.value)} className="px-3 py-2 border rounded-md text-sm" data-testid={`role-select-${p.id}`}>
                              <option value="agent">Agent</option><option value="admin">Admin</option><option value="super_admin">Super Admin</option>
                            </select>
                          )}
                          {agentInfo?.role === 'super_admin' && draft.id !== agentInfo?.id && (
                            <button onClick={() => setDeleteConfirm(!deleteConfirm)} className="flex items-center gap-1 px-3 py-2 bg-red-100 text-red-700 rounded-md text-sm hover:bg-red-200" data-testid={`delete-agent-${p.id}`}><Trash2 size={14} /> Delete</button>
                          )}
                        </div>
                        {/* Teams */}
                        {teams.length > 0 && (
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-xs font-bold text-gray-600">Teams:</span>
                            {teams.map(t => (
                              <label key={t.name} className="flex items-center gap-1 px-2 py-1.5 border rounded-md text-sm cursor-pointer hover:bg-gray-100 bg-white">
                                <input type="checkbox" checked={(draft.teams || []).includes(t.name)}
                                  onChange={(e) => { const cur = draft.teams || []; agentTeam(e.target.checked ? [...cur, t.name] : cur.filter(x => x !== t.name)); }}
                                  className="w-3.5 h-3.5" />
                                <span>{t.name}</span>
                              </label>
                            ))}
                          </div>
                        )}
                        {resetPwOpen && (
                          <div className="flex gap-2 items-center bg-white p-3 rounded-md border">
                            <div className="relative flex-1">
                              <input type={showNewPw ? 'text' : 'password'} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="New password (min 8 chars)" className="w-full px-3 py-2 pr-10 border rounded-md text-sm" data-testid={`reset-pw-input-${p.id}`} />
                              <button type="button" onClick={() => setShowNewPw(!showNewPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">{showNewPw ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                            </div>
                            <button onClick={agentResetPw} className="px-4 py-2 bg-brand-blue text-white rounded-md text-sm font-medium hover:bg-brand-orange" data-testid={`reset-pw-submit-${p.id}`}>Reset</button>
                          </div>
                        )}
                      </>
                    )}

                    {/* Delete confirm (shared) */}
                    {deleteConfirm && (
                      <div className="flex gap-2 items-center bg-red-50 p-3 rounded-md border border-red-200" data-testid={`delete-confirm-${p.id}`}>
                        <AlertTriangle size={16} className="text-red-600" />
                        <span className="text-sm text-red-700">Permanently delete this {p.kind === 'agent' ? 'agent' : 'contact'}?</span>
                        <button onClick={p.kind === 'agent' ? removeAgent : removeLead} className="px-3 py-1.5 bg-red-600 text-white rounded-md text-sm font-medium" data-testid={`confirm-delete-${p.id}`}>Yes, Delete</button>
                        <button onClick={() => setDeleteConfirm(false)} className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-md text-sm">Cancel</button>
                      </div>
                    )}

                    {/* Notes (shared) */}
                    <div className="bg-white p-3 rounded-md border">
                      <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1 mb-2"><StickyNote size={14} /> Notes</h4>
                      <div className="space-y-1 mb-2 max-h-32 overflow-y-auto">
                        {p.kind === 'agent'
                          ? rev(draft.adminNotes).map(n => (
                              <div key={n.id} className="text-xs text-gray-600 bg-gray-50 p-2 rounded"><span className="font-medium">{new Date(n.addedAt).toLocaleString()}</span>: {n.text}</div>
                            ))
                          : rev(draft.activityLog).map((n, i) => (
                              <div key={i} className="text-xs text-gray-600 bg-gray-50 p-2 rounded"><span className="font-medium capitalize">{n.action}</span> <span className="text-gray-400">{new Date(n.timestamp).toLocaleString()}</span> - {n.detail}</div>
                            ))}
                      </div>
                      <div className="flex gap-2">
                        <input type="text" value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add a note..." className="flex-1 px-3 py-1.5 border rounded-md text-sm" data-testid={`note-input-${p.id}`}
                          onKeyDown={(e) => e.key === 'Enter' && (p.kind === 'agent' ? noteAgent() : noteLead())} />
                        <button onClick={p.kind === 'agent' ? noteAgent : noteLead} className="px-3 py-1.5 bg-gray-800 text-white rounded-md text-sm font-medium hover:bg-gray-700" data-testid={`note-submit-${p.id}`}>Add</button>
                      </div>
                    </div>
                  </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* New Contact Modal */}
        {showCreate && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="new-contact-modal">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
                <h3 className="text-lg font-bold text-gray-900">Add New Contact</h3>
                <button onClick={() => setShowCreate(false)} className="text-gray-400 hover:text-gray-600" data-testid="close-new-contact"><X size={20} /></button>
              </div>
              <div className="p-5 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-xs font-bold text-gray-600 mb-1">First Name *</label><input value={newContact.firstName} onChange={(e) => setNewContact({ ...newContact, firstName: e.target.value })} className={inputCls} data-testid="nc-firstName" /></div>
                  <div><label className="block text-xs font-bold text-gray-600 mb-1">Last Name *</label><input value={newContact.lastName} onChange={(e) => setNewContact({ ...newContact, lastName: e.target.value })} className={inputCls} data-testid="nc-lastName" /></div>
                  <div><label className="block text-xs font-bold text-gray-600 mb-1">Email</label><input type="email" value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} className={inputCls} data-testid="nc-email" /></div>
                  <div><label className="block text-xs font-bold text-gray-600 mb-1">Phone *</label><input value={newContact.phone} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })} className={inputCls} data-testid="nc-phone" /></div>
                  <div><label className="block text-xs font-bold text-gray-600 mb-1">Zip Code</label><input value={newContact.zipCode} onChange={(e) => setNewContact({ ...newContact, zipCode: e.target.value })} className={inputCls} data-testid="nc-zip" /></div>
                  <div><label className="block text-xs font-bold text-gray-600 mb-1">Coverage Interest</label>
                    <select value={newContact.coverageInterest} onChange={(e) => setNewContact({ ...newContact, coverageInterest: e.target.value })} className={inputCls} data-testid="nc-coverage">
                      {Object.entries(coverageLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2"><label className="block text-xs font-bold text-gray-600 mb-1">Source</label>
                    <select value={newContact.source} onChange={(e) => setNewContact({ ...newContact, source: e.target.value })} className={inputCls} data-testid="nc-source">
                      {Object.entries(sourceLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </div>
                  <div className="col-span-2"><label className="block text-xs font-bold text-gray-600 mb-1">Notes</label><textarea rows={2} value={newContact.notes} onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })} className={inputCls} data-testid="nc-notes" /></div>
                </div>
                <p className="text-xs text-gray-400">Tip: After creating, open the contact in <b>Leads</b> to add full quote details (age, health, spouse, children).</p>
              </div>
              <div className="flex gap-2 p-4 border-t sticky bottom-0 bg-white">
                <button onClick={handleCreateContact} className="flex-1 bg-brand-orange text-white font-bold py-2.5 rounded-lg hover:bg-orange-600" data-testid="save-new-contact">Add Contact</button>
                <button onClick={() => setShowCreate(false)} className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50">Cancel</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AgentLayout>
  );
};

export default AdminUsers;
